/**
 * 文件上传服务
 */

export interface UploadResponse {
    success: boolean;
    message?: string;
    url?: string;
}

export interface StructuredData {
    统一社会信用代码?: string;
    企业名称?: string;
    [key: string]: any;
}

export interface ParseTaskResponse {
    success: boolean;
    message?: string;
    taskId?: string;
    fileUrl?: string;
    state?: string;
    structuredData?: string; // JSON字符串
}

/**
 * 上传营业执照并解析（同步返回结果）
 */
export const uploadAndParseBusinessLicense = async (
    file: File,
    wechatOpenId: string
): Promise<ParseTaskResponse> => {
    try {
        const apiUrl = '/api/file-parse/upload';

        // 构建FormData用于文件上传
        const formData = new FormData();
        formData.append('file', file);
        formData.append('wechatOpenId', wechatOpenId);

        console.log('上传并解析营业执照:', {
            fileName: file.name,
            fileSize: file.size,
            wechatOpenId
        });

        const response = await fetch(apiUrl, {
            method: 'POST',
            body: formData,
        });

        console.log('上传响应状态:', response.status, response.statusText);

        if (!response.ok) {
            const errorText = await response.text();
            console.error('上传失败响应:', {
                status: response.status,
                statusText: response.statusText,
                body: errorText
            });

            if (response.status === 400) {
                throw new Error('文件格式或大小不符合要求');
            } else if (response.status === 413) {
                throw new Error('文件太大，请上传小于200MB的文件');
            } else if (response.status === 404) {
                throw new Error('上传接口不存在，请联系技术支持');
            } else if (response.status >= 500) {
                throw new Error('服务器错误，请稍后重试');
            } else {
                throw new Error(`上传失败: ${response.status}`);
            }
        }

        const result = await response.json();
        console.log('上传成功:', result);

        if (result.success) {
            return {
                success: true,
                taskId: result.taskId,
                fileUrl: result.fileUrl,
                state: result.state,
                structuredData: result.structuredData,
                message: result.message || '上传成功',
            };
        } else {
            return {
                success: false,
                message: result.message || '上传失败',
            };
        }
    } catch (error) {
        console.error('上传文件错误:', error);

        if (error instanceof TypeError && error.message.includes('fetch')) {
            return {
                success: false,
                message: '网络错误，请检查网络连接后重试',
            };
        }

        return {
            success: false,
            message: error instanceof Error ? error.message : '上传失败，请稍后重试',
        };
    }
};

/**
 * 查询解析任务结果（如果需要轮询）
 */
export const queryParseTask = async (
    taskId: string
): Promise<ParseTaskResponse> => {
    try {
        const apiUrl = `/api/file-parse/query?taskId=${encodeURIComponent(taskId)}`;

        console.log('查询解析任务:', taskId);

        const response = await fetch(apiUrl, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
        });

        console.log('查询响应状态:', response.status, response.statusText);

        if (!response.ok) {
            throw new Error(`查询失败: ${response.status}`);
        }

        const result = await response.json();
        console.log('查询结果:', result);

        return {
            success: result.success,
            taskId: result.taskId,
            state: result.state,
            structuredData: result.structuredData,
            message: result.message,
        };
    } catch (error) {
        console.error('查询解析任务错误:', error);
        return {
            success: false,
            message: error instanceof Error ? error.message : '查询失败',
        };
    }
};

/**
 * 从结构化数据中提取营业执照信息
 */
export const extractBusinessLicenseInfo = (
    structuredDataStr: string
): { companyName?: string; businessLicenseNumber?: string } => {
    try {
        const info: { companyName?: string; businessLicenseNumber?: string } = {};

        // 解析JSON字符串
        const structuredData: StructuredData = JSON.parse(structuredDataStr);
        console.log('解析的结构化数据:', structuredData);

        // 提取企业名称
        if (structuredData['企业名称']) {
            info.companyName = structuredData['企业名称'];
        } else if (structuredData['名称']) {
            info.companyName = structuredData['名称'];
        } else if (structuredData['公司名称']) {
            info.companyName = structuredData['公司名称'];
        }

        // 提取统一社会信用代码
        if (structuredData['统一社会信用代码']) {
            info.businessLicenseNumber = structuredData['统一社会信用代码'];
        } else if (structuredData['信用代码']) {
            info.businessLicenseNumber = structuredData['信用代码'];
        } else if (structuredData['社会信用代码']) {
            info.businessLicenseNumber = structuredData['社会信用代码'];
        }

        console.log('提取的营业执照信息:', info);
        return info;
    } catch (error) {
        console.error('提取营业执照信息失败:', error);
        return {};
    }
};
